import { includes } from 'lodash';

import { readTaskListClipboardContent, type TaskListClipboardContent } from './taskListClipboardHtmlUtils';
import { duplicateTaskItem } from '../tiptap/extensions/taskList/taskListItemDuplicationUtils';
import { cleanHtmlTrailingEmptyParagraphs } from '../utils/htmlUtils';
import { DataTransferTypes } from '../media/mimeConstants';

// NOTE: Like `taskListClipboardHtmlUtils`, and unlike the rest of `src/common/taskLists/`, this
//  module is not server-safe - it parses through an extension list that pulls in React and SCSS.

/**
 * The task list element content a board paste should create, ready to build into an element.
 * The task items carry fresh ids by the time they are returned.
 */
export type TaskListPasteContent = Omit<TaskListClipboardContent, 'isProvenSingleEditorOrigin'>;

/** The markup every task list copy carries, whichever editor produced it. */
const TASK_LIST_HTML_MARKER = 'data-type="taskList"';

/**
 * Whether clipboard content that qualifies as a task list should be pasted as a task list element
 * rather than as a card.
 *
 * Flagged users get the conversion for anything that qualifies, deliberately including a legacy
 * checklist copied out of a card. Everyone else only gets it for content provably copied from a
 * single-editor task list, so an unflagged user pasting a checklist keeps today's card behaviour
 * but never loses chip data a single-editor list gave them.
 */
const shouldPasteAsTaskList = ({
    isProvenSingleEditorOrigin,
    isSingleTaskListEditorEnabled,
}: {
    isProvenSingleEditorOrigin: boolean;
    isSingleTaskListEditorEnabled: boolean;
}): boolean => isSingleTaskListEditorEnabled || isProvenSingleEditorOrigin;

/**
 * Reads the task list element content out of a board paste, returning nothing when the paste
 * should fall through to the existing card path instead.
 *
 * The HTML is cleaned the same way `handleHtml` cleans it, so this decides on exactly the content
 * the card path would otherwise build a card from. Pastes with no task list markup at all are
 * rejected before that, so an ordinary paste does not pay for a Tiptap parse it cannot use.
 *
 * Every task item is duplicated on the way out, stamping fresh `taskId`s and dropping inline
 * comment marks, for the same reasons the drag-copy path does: reminder reconciliation keys rows
 * by `(elementId, taskId)` on the new element's first create, and the copied comment threads
 * belong to the source the content was copied from.
 */
export const readTaskListPasteContent = ({
    clipboardData,
    isSingleTaskListEditorEnabled,
}: {
    clipboardData: DataTransfer;
    isSingleTaskListEditorEnabled: boolean;
}): TaskListPasteContent | null => {
    if (!includes(clipboardData.types, DataTransferTypes.HTML)) return null;

    const html = clipboardData.getData(DataTransferTypes.HTML);
    if (!html) return null;

    // Cleaned first, because that is what normalises the attribute quoting the marker looks for.
    const cleanHtml = cleanHtmlTrailingEmptyParagraphs(html);
    if (!cleanHtml.includes(TASK_LIST_HTML_MARKER)) return null;

    const clipboardContent = readTaskListClipboardContent(cleanHtml);
    if (!clipboardContent) return null;

    const { title, taskItems, isProvenSingleEditorOrigin } = clipboardContent;

    if (!shouldPasteAsTaskList({ isProvenSingleEditorOrigin, isSingleTaskListEditorEnabled })) return null;

    return {
        ...(title ? { title } : {}),
        taskItems: taskItems.map(duplicateTaskItem),
    };
};
